import { useState } from 'react'
import { View, Text } from 'react-native'
import { FlashList } from '@shopify/flash-list'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { CalendarOff } from '@/components/ui/icons'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useTourBookings, useRefundTour } from '@/lib/hooks/use-tours'
import { useToursCheckoutStore } from '@/lib/stores/tours-checkout.store'
import { toursPaymentErrorKey } from '@/lib/tours/payment-errors'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { formatCurrency } from '@/lib/utils/format'
import { formatTourDeparture } from '@/lib/tours/format'
import type { TourBookingSummary } from '@/lib/types/tours.types'

export default function ToursBookingsScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { data, isLoading, isRefetching, error, refetch } = useTourBookings()
  const refund = useRefundTour()
  const setCheckoutContext = useToursCheckoutStore((s) => s.setContext)

  const [pendingRefund, setPendingRefund] = useState<TourBookingSummary | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const statusLabel = (status: string): { label: string; color: string } => {
    if (status === 'paid') return { label: t('tours.booking_status.paid'), color: colors.success }
    if (status === 'cancelled')
      return { label: t('tours.booking_status.cancelled'), color: colors.textMuted }
    if (status === 'expired')
      return { label: t('tours.booking_status.expired'), color: colors.textMuted }
    if (status === 'completed')
      return { label: t('tours.booking_status.completed'), color: colors.textSecondary }
    if (status === 'in_progress')
      return { label: t('tours.booking_status.in_progress'), color: colors.textAccent }
    if (status === 'awaiting_payment' || status === 'partially_paid') {
      return { label: t('tours.booking_status.awaiting_payment'), color: colors.warning }
    }
    return { label: t('tours.booking_status.capacity_held'), color: colors.textAccent }
  }

  const runRefund = async () => {
    if (!pendingRefund) return
    const booking = pendingRefund
    setPendingRefund(null)
    setErrorKey(null)
    try {
      await refund.mutateAsync(booking.id)
    } catch (err) {
      setErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  const header = (
    <ScreenHeader
      title={t('tours.bookings.title')}
      backTestID="tours.bookings-back"
      onBackPress={() => router.back()}
    />
  )

  if (isLoading) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md, gap: SPACING.md }}>
          {[0, 1].map((index) => (
            <Card
              key={index}
              elevation="none"
              padding="none"
              radius="xl"
              className="overflow-hidden"
            >
              <View style={{ padding: SPACING.md, gap: SPACING.sm }}>
                <Skeleton width="60%" height={18} staggerIndex={index} />
                <Skeleton width="80%" height={14} staggerIndex={index} />
                <Skeleton width="40%" height={14} staggerIndex={index} />
              </View>
            </Card>
          ))}
        </View>
      </ScreenContainer>
    )
  }

  if (error) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<CalendarOff size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.bookings.error_title')}
            description={t('tours.bookings.error_body')}
            action={{ label: t('tours.retry'), onPress: () => void refetch() }}
          />
        </View>
      </ScreenContainer>
    )
  }

  if (!data || data.length === 0) {
    return (
      <ScreenContainer variant="dashboard">
        {header}
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<CalendarOff size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.bookings.empty_title')}
            description={t('tours.bookings.empty_body')}
            testID="tours.bookings-empty"
          />
        </View>
      </ScreenContainer>
    )
  }

  return (
    <ScreenContainer variant="dashboard" scroll={false}>
      {header}
      {errorKey ? (
        <Text
          style={{
            marginTop: SPACING.sm,
            paddingHorizontal: SPACING.lg,
            fontFamily: FONTS.body,
            fontSize: 13,
            color: colors.textError,
          }}
          testID="tours.bookings-error"
        >
          {t(errorKey)}
        </Text>
      ) : null}
      <FlashList
        data={data}
        keyExtractor={(item) => item.id}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingTop: SPACING.md,
          paddingBottom: SPACING['2xl'],
          paddingHorizontal: SPACING.lg,
        }}
        ItemSeparatorComponent={() => <View style={{ height: SPACING.md }} />}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => void refetch()} />
        }
        renderItem={({ item }) => {
          const status = statusLabel(item.status)
          const payable =
            (item.status === 'capacity_held' ||
              item.status === 'awaiting_payment' ||
              item.status === 'partially_paid') &&
            item.holdExpiresAt != null &&
            new Date(item.holdExpiresAt).getTime() > Date.now()
          return (
            <View testID={`tours.booking-${item.id}`}>
              <Card>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        fontSize: 17,
                        color: colors.textPrimary,
                      }}
                    >
                      {item.title ?? t('tours.bookings.unknown')}
                    </Text>
                    {item.startsAt ? (
                      <Text
                        style={{
                          marginTop: 2,
                          fontFamily: FONTS.body,
                          fontSize: 14,
                          color: colors.textSecondary,
                        }}
                      >
                        {formatTourDeparture(item.startsAt, i18n.language)}
                      </Text>
                    ) : null}
                  </View>
                  <View
                    style={{
                      paddingHorizontal: SPACING.sm,
                      paddingVertical: 3,
                      borderRadius: RADIUS.full,
                      backgroundColor: colors.surfaceSubtle,
                    }}
                  >
                    <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: status.color }}>
                      {status.label}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    marginTop: SPACING.md,
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
                    {item.passengerCount} · {t('tours.seats')}
                  </Text>
                  <Text
                    style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}
                  >
                    {formatCurrency(item.totalCents)}
                  </Text>
                </View>

                {payable ? (
                  <Button
                    title={t('tours.bookings.complete_payment')}
                    size="sm"
                    onPress={() => {
                      setCheckoutContext({
                        booking: {
                          id: item.id,
                          departureId: '',
                          organizerId: '',
                          passengerCount: item.passengerCount,
                          cargoM3: 0,
                          luggageSpec: [],
                          totalCents: item.totalCents,
                          status: 'capacity_held',
                          holdExpiresAt: item.holdExpiresAt ?? null,
                        },
                        title: item.title ?? t('tours.bookings.unknown'),
                        originLabel: item.originLabel ?? '',
                        destinationLabel: item.destinationLabel ?? '',
                        startsAt: item.startsAt ?? new Date().toISOString(),
                        luggageLabels: [],
                      })
                      router.push('/(tabs)/tours/checkout')
                    }}
                    style={{ marginTop: SPACING.md }}
                    testID={`tours.booking-pay-${item.id}`}
                  />
                ) : null}

                {['paid', 'in_progress', 'completed'].includes(item.status) ? (
                  <Button
                    title={t('tours.bookings.view_trip')}
                    variant="secondary"
                    size="sm"
                    onPress={() => router.push(`/(tabs)/tours/trip/${item.id}`)}
                    style={{ marginTop: SPACING.md }}
                    testID={`tours.booking-trip-${item.id}`}
                  />
                ) : null}

                {item.refundable ? (
                  <Button
                    title={t('tours.bookings.cancel')}
                    variant="secondary"
                    size="sm"
                    onPress={() => setPendingRefund(item)}
                    style={{ marginTop: SPACING.md }}
                    testID={`tours.booking-cancel-${item.id}`}
                  />
                ) : null}

                {item.status === 'completed' && !item.hasReview ? (
                  <Button
                    title={t('tours.reviews.write')}
                    variant="secondary"
                    size="sm"
                    onPress={() => router.push(`/(tabs)/tours/review/${item.id}`)}
                    style={{ marginTop: SPACING.md }}
                    testID={`tours.booking-review-${item.id}`}
                  />
                ) : item.hasReview ? (
                  <Text
                    style={{
                      marginTop: SPACING.md,
                      fontFamily: FONTS.body,
                      fontSize: 13,
                      color: colors.textMuted,
                    }}
                  >
                    {t('tours.reviews.reviewed')}
                  </Text>
                ) : null}
              </Card>
            </View>
          )
        }}
      />

      <ConfirmDialog
        visible={pendingRefund != null}
        title={t('tours.bookings.cancel_confirm_title')}
        message={t('tours.bookings.cancel_confirm_body')}
        confirmLabel={t('tours.bookings.cancel_confirm_yes')}
        cancelLabel={t('tours.bookings.cancel_confirm_no')}
        destructive
        onConfirm={runRefund}
        onCancel={() => setPendingRefund(null)}
      />
    </ScreenContainer>
  )
}
